import React, { useState, useEffect } from "react";
import { FiSearch, FiFilter, FiDownload, FiEye, FiEdit, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import adminAxios from "../../utils/adminAxios";
import UserProfileModal from "./userProfileModal";

const Users = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const openProfile = (userId) => {
    setSelectedUserId(userId);
    setProfileModalOpen(true);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(user =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.number?.includes(searchTerm)
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Simulate API call - replace with your actual API
      setTimeout(async () => {
        try {
          const response = await adminAxios.get("users/get-all-users");
          setUsers(response.data.data || []);
        } catch (error) {
          console.error('API Error:', error);
          // Fallback mock data
          setUsers([
            { id: 1, name: "John Doe", number: "1234567890" },
            { id: 2, name: "Jane Smith", number: "0987654321" },
            { id: 3, name: "Bob Johnson", number: "5555555555" },
          ]);
        }
      }, 1000);
    } catch (error) {
      toast.error(t("dashboard.users.toast.fetchError", { message: error.message }));
      // Fallback mock data
      setUsers([
        { id: 1, name: "John Doe", number: "1234567890" },
        { id: 2, name: "Jane Smith", number: "0987654321" },
        { id: 3, name: "Bob Johnson", number: "5555555555" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const UserCard = ({ user }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300 animate-fadeIn">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold text-lg">
              {user.name?.charAt(0) || "U"}
            </span>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {user.name || t("dashboard.users.unknownUser")}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {user.number || t("dashboard.users.noNumber")}
            </p>
          </div>
        </div>
        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium">
          {t("dashboard.users.status.active")}
        </span>
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {t("dashboard.users.id")}: {user.id}
        </div>
        <div className="flex space-x-2 rtl:space-x-reverse">
          <button
            onClick={() => openProfile(user.id)}
            className="p-2 text-gray-500 hover:text-blue-500 transition-colors"
          >
            <FiEye className="w-4 h-4" />
          </button>
          <button className="p-2 text-gray-500 hover:text-green-500 transition-colors">
            <FiEdit className="w-4 h-4" />
          </button>
          <button className="p-2 text-gray-500 hover:text-red-500 transition-colors">
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const UserTable = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden animate-fadeIn">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
              <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("dashboard.users.table.user")}
              </th>
              <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("dashboard.users.table.phoneNumber")}
              </th>
              <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("dashboard.users.table.status")}
              </th>
              <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("dashboard.users.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredUsers.map((user) => (
              <tr
                key={user.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors animate-fadeIn"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                      <span className="text-white text-sm font-semibold">
                        {user.name?.charAt(0) || "U"}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.name || t("dashboard.users.unknownUser")}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t("dashboard.users.id")}: {user.id}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {user.number || t("dashboard.users.notAvailable")}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    {t("dashboard.users.status.active")}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2 rtl:space-x-reverse">
                    <button
                      onClick={() => openProfile(user.id)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      {t("dashboard.users.actions.view")}
                    </button>
                    <button className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 transition-colors">
                      {t("dashboard.users.actions.edit")}
                    </button>
                    <button className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors">
                      {t("dashboard.users.actions.delete")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("dashboard.users.title")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t("dashboard.users.subtitle")}
          </p>
        </div>
        <div className="flex items-center space-x-3 rtl:space-x-reverse mt-4 lg:mt-0">
          <button className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <FiDownload className="w-4 h-4" />
            <span>{t("dashboard.users.export")}</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white animate-fadeIn">
          <h3 className="text-lg font-semibold">{t("dashboard.users.stats.totalUsers.title")}</h3>
          <p className="text-3xl font-bold mt-2">{users.length}</p>
          <p className="text-blue-100 text-sm mt-1">{t("dashboard.users.stats.totalUsers.subtitle")}</p>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white animate-fadeIn" style={{animationDelay: '100ms'}}>
          <h3 className="text-lg font-semibold">{t("dashboard.users.stats.activeToday.title")}</h3>
          <p className="text-3xl font-bold mt-2">{Math.floor(users.length * 0.3)}</p>
          <p className="text-green-100 text-sm mt-1">{t("dashboard.users.stats.activeToday.subtitle")}</p>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white animate-fadeIn" style={{animationDelay: '200ms'}}>
          <h3 className="text-lg font-semibold">{t("dashboard.users.stats.newThisWeek.title")}</h3>
          <p className="text-3xl font-bold mt-2">{Math.floor(users.length * 0.1)}</p>
          <p className="text-purple-100 text-sm mt-1">{t("dashboard.users.stats.newThisWeek.subtitle")}</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="relative flex-1 max-w-md">
          <FiSearch className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t("dashboard.users.search.placeholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 rtl:pl-4 rtl:pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>
        <div className="flex space-x-3 rtl:space-x-reverse">
          <button className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <FiFilter className="w-4 h-4" />
            <span>{t("dashboard.users.filter")}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="lg:hidden">
        {/* Mobile View - Cards */}
        <div className="grid grid-cols-1 gap-4">
          {filteredUsers.map((user) => (
            <UserCard key={user.id} user={user} />
          ))}
        </div>
      </div>
      
      <div className="hidden lg:block">
        {/* Desktop View - Table */}
        <UserTable />
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && !loading && (
        <div className="text-center py-12 animate-fadeIn">
          <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">👥</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t("dashboard.users.empty.title")}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm ? t("dashboard.users.empty.searchHint") : t("dashboard.users.empty.noUsers")}
          </p>
        </div>
      )}

      <UserProfileModal
        open={profileModalOpen}
        userId={selectedUserId}
        onClose={() => setProfileModalOpen(false)}
      />

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Users;